
import React, { useState, useEffect, useCallback } from 'react';
import { adminService, SystemSettings } from '../services/adminService';
import { User, Identity, LoginLog } from '../types';
import { 
  Users, Search, RefreshCw, Monitor, 
  Smartphone, ShieldCheck as ShieldIcon,
  ChevronRight, Trash2, Edit3, Save, Globe, 
  Clock, Fingerprint, MapPin, Activity,
  ChevronDown, UserCheck, X, CheckSquare, Square,
  ShieldAlert, Download, Eye, EyeOff, Copy, Check,
  Key as KeyIcon, User as UserIcon
} from 'lucide-react';

const AdminDashboard: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<{ user: any, history: Identity[], logs: LoginLog[] } | null>(null);
  const [showFullAudit, setShowFullAudit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  
  // 批量选择状态
  const [selectedUsernames, setSelectedUsernames] = useState<Set<string>>(new Set());
  
  // 全局配置状态
  const [isDailyResetEnabled, setIsDailyResetEnabled] = useState(false);
  const [dailyResetValue, setDailyResetValue] = useState(5);
  const [updatingSettings, setUpdatingSettings] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersData, settingsData] = await Promise.all([
        adminService.getAllUsers().catch(() => []),
        adminService.getSettings().catch(() => null)
      ]);
      setUsers(usersData);
      if (settingsData) {
        setSettings(settingsData);
        setIsDailyResetEnabled(settingsData.daily_reset_enabled);
        setDailyResetValue(settingsData.daily_reset_limit);
      }
    } catch (err) {
      console.error("加载数据失败", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleSelectAll = () => {
    if (selectedUsernames.size === filteredUsers.length) {
      setSelectedUsernames(new Set());
    } else {
      setSelectedUsernames(new Set(filteredUsers.map(u => u.username)));
    }
  };

  const toggleSelectUser = (e: React.MouseEvent, username: string) => {
    e.stopPropagation();
    const next = new Set(selectedUsernames);
    if (next.has(username)) next.delete(username);
    else next.add(username);
    setSelectedUsernames(next);
  };

  const handleBatchDelete = async () => {
    if (!confirm(`确定要永久删除这 ${selectedUsernames.size} 个账户及其所有历史记录吗？`)) return;
    try {
      setLoading(true);
      for (const username of selectedUsernames) {
        await adminService.deleteUser(username);
      }
      setSelectedUsernames(new Set());
      await loadData();
    } catch (e) {
      alert("部分操作失败");
    } finally {
      setLoading(false);
    }
  };

  const handleBatchUpdateQuota = async () => {
    const val = window.prompt(`批量设置 ${selectedUsernames.size} 个用户的额度为:`, "50");
    if (!val) return;
    const newQuota = parseInt(val);
    if (isNaN(newQuota)) return;

    try {
      setLoading(true);
      for (const username of selectedUsernames) {
        await adminService.updateQuota(username, newQuota);
      }
      setSelectedUsernames(new Set());
      await loadData();
    } catch (e) {
      alert("操作失败");
    } finally {
      setLoading(false);
    }
  };

  // Fix: Added missing handleUpdateQuota function to update quota for a single user
  const handleUpdateQuota = async (username: string) => {
    const val = window.prompt(`设置用户 ${username} 的额度为:`, "50");
    if (!val) return;
    const newQuota = parseInt(val);
    if (isNaN(newQuota)) return;

    try {
      setLoading(true);
      const success = await adminService.updateQuota(username, newQuota);
      if (success) {
        if (selectedUser && selectedUser.user.username === username) {
          setSelectedUser({
            ...selectedUser,
            user: { ...selectedUser.user, quota: newQuota }
          });
        }
        await loadData();
      }
    } catch (e) {
      alert("操作失败");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSystemSettings = async () => {
    setUpdatingSettings(true);
    try {
      await adminService.updateSettings({
        daily_reset_enabled: isDailyResetEnabled,
        daily_reset_limit: dailyResetValue
      });
      alert("全局重置策略更新成功！所有用户额度将在每日 00:00 自动校准至 " + dailyResetValue);
    } catch (e) { alert("更新失败"); }
    finally { setUpdatingSettings(false); }
  };

  const viewUserDetail = async (username: string) => {
    try {
      setShowPassword(false);
      const data = await adminService.getUserDetail(username);
      setSelectedUser({ user: data.user, history: data.history, logs: data.loginLogs });
    } catch (e) {
      alert("无法加载用户详情");
    }
  };

  const exportUsers = () => {
    const dataStr = JSON.stringify(users, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `identity_pro_users_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const filteredUsers = users.filter(u => 
    u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.email && u.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20 relative">
      {/* 批量操作悬浮条 */}
      {selectedUsernames.size > 0 && (
        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white px-8 py-4 rounded-[2rem] shadow-2xl flex items-center gap-8 animate-in slide-in-from-bottom-10 border border-white/10 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center font-black">
              {selectedUsernames.size}
            </div>
            <div>
              <p className="text-xs font-black">已选中成员</p>
              <p className="text-[10px] text-slate-400">正在进行批量管理</p>
            </div>
          </div>
          <div className="h-10 w-[1px] bg-white/10"></div>
          <div className="flex items-center gap-4">
            <button 
              onClick={handleBatchUpdateQuota}
              className="flex items-center gap-2 px-6 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-xs font-bold transition-all"
            >
              <Edit3 size={14} /> 批量设额
            </button>
            <button 
              onClick={handleBatchDelete}
              className="flex items-center gap-2 px-6 py-2.5 bg-red-500/80 hover:bg-red-600 rounded-xl text-xs font-bold transition-all shadow-lg shadow-red-500/20"
            >
              <Trash2 size={14} /> 批量销毁
            </button>
            <button 
              onClick={() => setSelectedUsernames(new Set())}
              className="p-2 text-slate-400 hover:text-white transition-all"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {/* 顶部配置面板 */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="bg-white rounded-[2.5rem] border border-slate-200 p-8 shadow-sm flex items-center gap-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-5"><Users size={80} /></div>
          <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-3xl flex items-center justify-center shadow-inner">
            <ShieldIcon size={32} />
          </div>
          <div>
            <h2 className="text-2xl font-black text-slate-900">管理控制台</h2>
            <p className="text-slate-500 text-sm font-medium">当前监控 {users.length} 名注册成员</p>
            <div className="flex gap-4 mt-3">
              <span className="text-[10px] font-black bg-blue-600 text-white px-2 py-0.5 rounded uppercase">系统在线</span>
              <span className="text-[10px] font-black bg-green-100 text-green-700 px-2 py-0.5 rounded uppercase">正常</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-[2.5rem] border border-slate-200 p-8 shadow-sm col-span-1 xl:col-span-2 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center">
              <Globe size={24} />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 text-sm">全局重置策略</h3>
              <p className="text-xs text-slate-500">每日 00:00 自动校准全员生成额度</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-3xl border border-slate-100 w-full md:w-auto">
            <div className="flex items-center gap-3 px-2">
              <span className="text-xs font-bold text-slate-700">每日清零</span>
              <button 
                onClick={() => setIsDailyResetEnabled(!isDailyResetEnabled)}
                className={`w-12 h-6 rounded-full transition-all relative ${isDailyResetEnabled ? 'bg-blue-600' : 'bg-slate-300'}`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${isDailyResetEnabled ? 'left-7' : 'left-1'}`}></div>
              </button>
            </div>
            <div className="h-8 w-[1px] bg-slate-200 hidden md:block"></div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black text-slate-400 uppercase">重置为:</span>
              <input 
                type="number" 
                value={dailyResetValue} 
                onChange={e => setDailyResetValue(parseInt(e.target.value) || 0)}
                className="w-16 px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-black text-center focus:outline-none focus:border-blue-400"
              />
            </div>
            <button 
              onClick={handleUpdateSystemSettings}
              disabled={updatingSettings}
              className="px-6 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-black flex items-center gap-2 hover:bg-blue-700 transition-all shadow-lg active:scale-95"
            >
              {updatingSettings ? <RefreshCw className="animate-spin" size={14} /> : <Save size={14} />}
              更新配置
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* 用户列表 */}
        <div className={`lg:col-span-${selectedUser ? '7' : '12'} bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col transition-all duration-300`}>
          <div className="p-8 border-b border-slate-100 flex items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input 
                type="text" 
                placeholder="搜索成员名、邮箱、注册 IP..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:border-blue-400 transition-all text-sm font-semibold"
              />
            </div>
            <div className="flex items-center gap-3 ml-4">
              <button 
                onClick={exportUsers} 
                className="p-3 bg-slate-50 text-slate-600 hover:text-blue-600 rounded-2xl transition-all flex items-center gap-2 text-xs font-black"
                title="导出所有用户数据"
              >
                <Download size={18} /> 导出
              </button>
              <button onClick={toggleSelectAll} className="p-3 bg-slate-50 text-slate-600 hover:text-blue-600 rounded-2xl transition-all flex items-center gap-2 text-xs font-black">
                {selectedUsernames.size === filteredUsers.length ? <CheckSquare size={18} /> : <Square size={18} />}
                {selectedUsernames.size > 0 ? `已选 ${selectedUsernames.size}` : '全选'}
              </button>
              <button onClick={loadData} className="p-3 bg-slate-50 text-slate-400 hover:text-blue-600 rounded-2xl transition-all">
                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50/50">
                  <th className="px-6 py-4 w-12"></th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">成员画像</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">额度负载</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">最后活跃</th>
                  <th className="px-6 py-4 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredUsers.map(u => (
                  <tr 
                    key={u.username} 
                    onClick={() => viewUserDetail(u.username)}
                    className={`hover:bg-blue-50/30 cursor-pointer transition-all group ${selectedUser?.user.username === u.username ? 'bg-blue-50' : ''} ${selectedUsernames.has(u.username) ? 'bg-blue-50/80' : ''}`}
                  >
                    <td className="px-6 py-5">
                      <button 
                        onClick={(e) => toggleSelectUser(e, u.username)}
                        className={`transition-all ${selectedUsernames.has(u.username) ? 'text-blue-600' : 'text-slate-300'}`}
                      >
                        {selectedUsernames.has(u.username) ? <CheckSquare size={18} /> : <Square size={18} />}
                      </button>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center font-black group-hover:bg-blue-600 group-hover:text-white transition-all">
                          {u.username.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-black text-slate-900">{u.username}</p>
                            {u.role === 'admin' && <span className="text-[8px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-black uppercase tracking-tighter">Admin</span>}
                          </div>
                          <p className="text-[10px] text-slate-400 font-medium truncate max-w-[120px]">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="w-32">
                         <div className="flex justify-between text-[10px] font-black mb-1">
                           <span className="text-blue-600">{u.used_quota}</span>
                           <span className="text-slate-400">/ {u.quota}</span>
                         </div>
                         <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                           <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${Math.min(100, (u.used_quota / u.quota) * 100)}%` }}></div>
                         </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-2">
                        {u.last_login_device?.includes('pc') || u.last_login_device?.includes('Windows') ? <Monitor size={14} className="text-slate-400" /> : <Smartphone size={14} className="text-slate-400" />}
                        <div>
                          <p className="text-[10px] font-mono font-bold text-slate-700">{u.last_login_ip || '127.0.0.1'}</p>
                          <p className="text-[9px] text-slate-400 font-medium truncate max-w-[100px]">{u.last_login_device || 'Unknown'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 text-right">
                       <ChevronRight size={18} className="text-slate-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 详情侧面板 */}
        {selectedUser && (
           <div className="lg:col-span-5 space-y-6 animate-in slide-in-from-right-8 duration-500 max-h-[1200px] overflow-y-auto pb-10 custom-scrollbar pr-1">
             <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden shadow-2xl">
               <div className="absolute top-0 right-0 p-8 opacity-10"><Activity size={100} /></div>
               <div className="flex items-start justify-between relative z-10">
                 <div className="flex items-center gap-5">
                   <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center text-2xl font-black">
                     {selectedUser.user.username.charAt(0).toUpperCase()}
                   </div>
                   <div>
                     <h2 className="text-xl font-black">{selectedUser.user.username}</h2>
                     <p className="text-xs text-blue-300 font-mono mt-1">{selectedUser.user.email}</p>
                   </div>
                 </div>
                 <button onClick={() => setSelectedUser(null)} className="p-2 hover:bg-white/10 rounded-xl transition-all"><ChevronDown size={24} /></button>
               </div>
               <div className="grid grid-cols-2 gap-4 mt-8 relative z-10">
                 <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                   <p className="text-[10px] font-bold text-blue-300 uppercase tracking-widest mb-1">当前配额</p>
                   <div className="flex items-center justify-between">
                     <span className="text-lg font-black">{selectedUser.user.quota}</span>
                     <button onClick={() => handleUpdateQuota(selectedUser.user.username)} className="p-1.5 hover:bg-white/20 rounded-lg"><Edit3 size={14} /></button>
                   </div>
                 </div>
                 <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                   <p className="text-[10px] font-bold text-blue-300 uppercase tracking-widest mb-1">注册时间</p>
                   <span className="text-xs font-bold">{new Date(selectedUser.user.createdAt).toLocaleDateString()}</span>
                 </div>
               </div>
             </div>

             {/* 账号密码查看卡片 */}
             <div className="bg-white rounded-[2rem] border border-slate-200 p-8 shadow-sm">
               <h3 className="font-black text-slate-900 flex items-center gap-2 mb-6">
                 <ShieldIcon size={18} className="text-blue-600" /> 账号凭据 (Credentials)
               </h3>
               <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <UserIcon size={16} className="text-slate-400" />
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">用户名</p>
                        <p className="text-sm font-bold text-slate-900">{selectedUser.user.username}</p>
                      </div>
                    </div>
                    <button onClick={() => copyToClipboard(selectedUser.user.username, 'username')} className="p-2 text-slate-400 hover:text-blue-600 transition-colors">
                      {copiedField === 'username' ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                    </button>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <KeyIcon size={16} className="text-slate-400" />
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">登录密码</p>
                        <p className="text-sm font-mono font-bold text-slate-900">
                          {showPassword ? selectedUser.user.password : '••••••••••••'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setShowPassword(!showPassword)} className="p-2 text-slate-400 hover:text-blue-600 transition-colors">
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      <button onClick={() => copyToClipboard(selectedUser.user.password, 'password')} className="p-2 text-slate-400 hover:text-blue-600 transition-colors">
                        {copiedField === 'password' ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>
               </div>
             </div>

             <div className="bg-white rounded-[2rem] border border-slate-200 p-8 shadow-sm">
               <div className="flex items-center justify-between mb-6">
                 <h3 className="font-black text-slate-900 flex items-center gap-2"><Clock size={18} className="text-blue-600" /> 登录历史 (Audit)</h3>
               </div>
               <div className="space-y-4">
                 {selectedUser.logs.slice(0, 5).map(log => (
                   <div key={log.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                     <div className="flex items-center gap-3">
                       <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400">
                         {log.device_type === 'pc' ? <Monitor size={14} /> : <Smartphone size={14} />}
                       </div>
                       <div>
                         <p className="text-xs font-bold text-slate-800">{log.ip}</p>
                         <p className="text-[9px] text-slate-400 font-medium">{log.location}</p>
                       </div>
                     </div>
                     <div className="text-right">
                       <p className="text-[9px] font-black text-slate-400 uppercase">{new Date(log.timestamp).toLocaleTimeString()}</p>
                     </div>
                   </div>
                 ))}
               </div>
             </div>
           </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
